import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "../App";
import { Button } from "./ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

const TYPE_LABEL = {
  phone: "Phone",
  on_site: "On Site",
  meeting: "Meeting",
  email: "Email",
  other: "Other",
};

export default function ClientCommsPublic() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/public/client-comms/${token}`);
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setState({ loading: false, error: e.detail || "This link is no longer active.", data: null });
        return;
      }
      setState({ loading: false, error: null, data: await res.json() });
    } catch {
      setState({ loading: false, error: "Could not load this record.", data: null });
    }
  };

  useEffect(() => {
    load();
  }, [token]); // eslint-disable-line

  const acknowledge = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/client-comms/${token}/acknowledge`, {
        method: "POST",
      });
      if (res.ok) {
        await load();
      } else {
        const e = await res.json().catch(() => ({}));
        alert(e.detail || "Could not acknowledge");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="public-conv-error">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold text-slate-800">Link unavailable</p>
          <p className="text-slate-500 mt-2">{state.error}</p>
        </div>
      </div>
    );
  }

  const d = state.data;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-conv-page">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.png?v=3" alt="SiteProApp" className="w-9 h-9 object-contain" />
          <div>
            <h1 className="font-bold text-slate-900">Site Communication Record</h1>
            <p className="text-sm text-slate-500">{d.site_address || d.site_name}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-5 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Date</span>
            <span className="font-medium text-slate-800">{d.date}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">Type</span>
            <span className="font-medium text-slate-800">{TYPE_LABEL[d.type]}</span>
          </div>
          <div className="text-sm">
            <p className="text-slate-500">Subject</p>
            <p className="font-semibold text-slate-900">{d.subject}</p>
          </div>
          <div className="text-sm">
            <p className="text-slate-500">Summary</p>
            <p className="text-slate-800 whitespace-pre-wrap">{d.summary}</p>
          </div>
          <p className="text-xs text-slate-400 pt-2 border-t">
            Logged by {d.builder_name}
            {d.created_at ? ` on ${new Date(d.created_at).toLocaleDateString("en-AU")}` : ""}
          </p>
        </div>

        <div className="mt-6">
          {d.acknowledged ? (
            <div
              className="bg-green-50 border border-green-200 rounded-2xl p-5 flex items-center gap-3"
              data-testid="public-conv-acknowledged"
            >
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-800">Acknowledged</p>
                <p className="text-sm text-green-700">
                  {d.acknowledged_at ? new Date(d.acknowledged_at).toLocaleString("en-AU") : ""}
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm border p-5">
              <p className="text-sm text-slate-600 mb-4">
                By tapping Acknowledge, you confirm you have read and understood the
                above record of your conversation with your builder.
              </p>
              <Button
                onClick={acknowledge}
                disabled={submitting}
                className="w-full bg-amber-500 hover:bg-amber-600 h-12 text-base"
                data-testid="public-conv-ack-btn"
              >
                {submitting ? "Submitting..." : "Acknowledge"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
